import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { filter, map, withLatestFrom } from 'rxjs/operators';
import { subscribeToExternalContactNotifications, updateContact } from '../actions/external-contact.actions';
import { ContactNotificationAction } from '../models/contact-notification-action';
import { ExternalContactNotificationMessage } from '../models/external-contact-notification-message';
import { externalContactTopicName } from '../helpers/external-contact-topics';
import { UserPermission } from '../../users/models/user-permission.enum';
import {
  notificationChannelCreated,
  subscribeToNotifications,
  notificationArrived
} from '../../notifications/actions/notification.actions';
import { selectUserHasPermission } from '../../users/selectors/permission.selectors';
import { selectUser } from '../../users/selectors/user.selectors';

@Injectable({
  providedIn: 'root'
})
export class GenesysCloudExternalContactNotificationEffects {
  readonly subscribeToExternalContactNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(subscribeToExternalContactNotifications),
      concatLatestFrom(() => this.store.select(selectUserHasPermission(UserPermission.ExternalContactView))),
      filter(([_, hasPermission]) => hasPermission),
      map((contactId, _) => contactId),
      concatLatestFrom(() => this.store.select(selectUser)),
      withLatestFrom(this.actions$.pipe(ofType(notificationChannelCreated))), // eslint-disable-line ngrx/prefer-concat-latest-from
      map(([[[{ contactId }], user], { channel }]) => subscribeToNotifications({
        userId: user.id,
        channelId: channel.id,
        topics: [externalContactTopicName(contactId)]
      }))
    )
  );

  readonly listenOnContactUpdated$ = createEffect(() => this.actions$.pipe(
    ofType(notificationArrived),
    filter(({ notification }: { notification: ExternalContactNotificationMessage }) =>
      notification.topicName === externalContactTopicName(notification.eventBody.id)
      && notification.metadata?.action === ContactNotificationAction.Update
    ),
    map(({ notification }) => updateContact({ contactId: notification.eventBody.id }))
  ));

  constructor(private readonly actions$: Actions,
    private readonly store: Store
  ) {}
}

